import { storableError } from '../../util/errors';
import { initiateRecurringPayment, getSeatGroup, getSubscriptionPrices } from '../../util/api';

// ================ Action types ================ //

export const SUBSCRIBE_REQUEST = 'app/SubscriptionsPage/SUBSCRIBE_REQUEST';
export const SUBSCRIBE_SUCCESS = 'app/SubscriptionsPage/SUBSCRIBE_SUCCESS';
export const SUBSCRIBE_ERROR = 'app/SubscriptionsPage/SUBSCRIBE_ERROR';

export const FETCH_SEAT_GROUP_REQUEST = 'app/SubscriptionsPage/FETCH_SEAT_GROUP_REQUEST';
export const FETCH_SEAT_GROUP_SUCCESS = 'app/SubscriptionsPage/FETCH_SEAT_GROUP_SUCCESS';
export const FETCH_SEAT_GROUP_ERROR = 'app/SubscriptionsPage/FETCH_SEAT_GROUP_ERROR';

export const FETCH_PRICES_REQUEST = 'app/SubscriptionsPage/FETCH_PRICES_REQUEST';
export const FETCH_PRICES_SUCCESS = 'app/SubscriptionsPage/FETCH_PRICES_SUCCESS';
export const FETCH_PRICES_ERROR = 'app/SubscriptionsPage/FETCH_PRICES_ERROR';

// ================ Reducer ================ //

const initialState = {
    subscribeInProgress: false,
    subscribeError: null,
    sessionURL: null,
    seatGroup: null,
    seatGroupRole: null,
    seatGroupInProgress: false,
    // Keyed by subscription name, in pence. Empty until the server answers,
    // and the page falls back to its own defaults until then.
    prices: {},
    pricesInProgress: false,
};

const subscriptionsPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
        case SUBSCRIBE_REQUEST:
            return { ...state, subscribeInProgress: true, subscribeError: null };
        case SUBSCRIBE_SUCCESS:
            return { ...state, subscribeInProgress: false, sessionURL: payload };
        case SUBSCRIBE_ERROR:
            return { ...state, subscribeInProgress: false, subscribeError: payload };

        case FETCH_SEAT_GROUP_REQUEST:
            return { ...state, seatGroupInProgress: true };
        case FETCH_SEAT_GROUP_SUCCESS:
            return {
                ...state,
                seatGroupInProgress: false,
                seatGroup: payload.group,
                seatGroupRole: payload.role,
            };
        case FETCH_SEAT_GROUP_ERROR:
            return { ...state, seatGroupInProgress: false, seatGroup: null, seatGroupRole: null };

        case FETCH_PRICES_REQUEST:
            return { ...state, pricesInProgress: true };
        case FETCH_PRICES_SUCCESS:
            return { ...state, pricesInProgress: false, prices: payload };
        case FETCH_PRICES_ERROR:
            return { ...state, pricesInProgress: false };

        default:
            return state;
    }
};

export default subscriptionsPageReducer;

// ================ Action creators ================ //

export const subscribeRequest = () => ({ type: SUBSCRIBE_REQUEST });
export const subscribeSuccess = sessionURL => ({ type: SUBSCRIBE_SUCCESS, payload: sessionURL });
export const subscribeError = e => ({ type: SUBSCRIBE_ERROR, error: true, payload: e });

export const fetchSeatGroupRequest = () => ({ type: FETCH_SEAT_GROUP_REQUEST });
export const fetchSeatGroupSuccess = payload => ({ type: FETCH_SEAT_GROUP_SUCCESS, payload });
export const fetchSeatGroupError = () => ({ type: FETCH_SEAT_GROUP_ERROR, error: true });

export const fetchPricesRequest = () => ({ type: FETCH_PRICES_REQUEST });
export const fetchPricesSuccess = payload => ({ type: FETCH_PRICES_SUCCESS, payload });
export const fetchPricesError = () => ({ type: FETCH_PRICES_ERROR, error: true });

// ================ Thunks ================ //

export const subscribe = (params) => (dispatch, getState, sdk) => {
    dispatch(subscribeRequest());

    return initiateRecurringPayment(params)
        .then(response => {
            const { sessionURL, success, message } = response;

            if (success) {
                dispatch(subscribeSuccess(sessionURL));
            } else {
                // The server sends a message when it refuses the seat
                // selection, for example an email that is already subscribed.
                dispatch(subscribeError(message || "Something went wrong"));
            };

            return sessionURL;
        })
        .catch(e => {
            dispatch(subscribeError(storableError(e)));
            throw e;
        });
};

export const fetchSeatGroup = () => (dispatch, getState, sdk) => {
    dispatch(fetchSeatGroupRequest());

    return getSeatGroup({})
        .then(response => {
            dispatch(fetchSeatGroupSuccess({
                group: response?.group || null,
                role: response?.role || null,
            }));
        })
        .catch(() => {
            // The seat panel is extra information, so a failure here should
            // never stop the rest of the page from working.
            dispatch(fetchSeatGroupError());
        });
};

export const fetchSubscriptionPrices = () => (dispatch, getState, sdk) => {
    dispatch(fetchPricesRequest());

    return getSubscriptionPrices({})
        .then(response => {
            // A map is easier to read from than a list, because the page looks
            // prices up by subscription name.
            const prices = (response?.prices || []).reduce((acc, item) => {
                acc[item.id] = item.price;
                return acc;
            }, {});

            dispatch(fetchPricesSuccess(prices));
        })
        .catch(() => {
            // The page keeps its own default prices, so a failure here shows
            // the last agreed numbers rather than an empty price.
            dispatch(fetchPricesError());
        });
};
